import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield } from 'lucide-react';

interface TwoFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => Promise<void>;
  loading?: boolean;
  error?: string;
}

export const TwoFactorModal = ({ 
  isOpen, 
  onClose, 
  onVerify, 
  loading = false, 
  error 
}: TwoFactorModalProps) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [localLoading, setLocalLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code) {
      setLocalError('');
      setLocalLoading(true);
      try {
        await onVerify(code);
      } catch (err: unknown) {
        let errorMessage = 'Error al verificar el código';
        if (err && typeof err === 'object' && 'message' in err) {
          errorMessage = String(err.message);
        }
        setLocalError(errorMessage);
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const handleClose = () => {
    setCode('');
    setLocalError('');
    setLocalLoading(false);
    onClose();
  };
  
  const displayError = error || localError;
  const displayLoading = loading || localLoading;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Autenticación de Dos Factores
          </DialogTitle>
          <DialogDescription>
            Ingresa el código de 6 dígitos de tu aplicación de autenticación
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="2fa-code">Código de verificación</Label>
            <Input
              id="2fa-code"
              type="text"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              className="text-center text-2xl font-mono tracking-widest bg-peach/20 border-2 border-primary/30 focus:border-primary focus:bg-white transition-all duration-200"
              disabled={displayLoading}
              autoFocus
            />
          </div>

          {displayError && (
            <Alert variant="destructive">
              <AlertDescription>{displayError}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={displayLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={displayLoading || code.length !== 6}
              className="flex-1"
            >
              {displayLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Verificar'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};